import { useCallback, useContext, useMemo } from 'react';
import { AdminRegisterModal } from '@/modules/admin-dashboard/components/modals/AdminRegisterModal.tsx';
import { useGenerateRegisterCertificatesMutation } from '@/modules/admin-dashboard/queries/certificates.ts';
import { useAdminDeleteUserRegisterMutation } from '@/modules/admin-dashboard/queries/registers.tsx';
import { Avatar, ConfirmModal, ConfirmModalProps, DropdownMenu, ModalProviderContext } from 'ui';
import { DotsHorizontalBlueIcon } from 'ui/icons';
import { goodFormatUSDCurrency } from 'ui/utils';
import { RegisterWithJoinResponse } from 'api';

export interface AdminRegisterItemProps {
  register: RegisterWithJoinResponse;
  index: number;
}

export const AdminRegisterItem = ({ register, index }: AdminRegisterItemProps) => {
  const { openModal, onCloseAll } = useContext(ModalProviderContext);
  const { id, name, total_invested } = register;

  const { mutateAsync: deleteRegister } = useAdminDeleteUserRegisterMutation({
    onSuccess: onCloseAll,
  });

  const handleOpenRedactRegisterModal = useCallback(() => {
    openModal(AdminRegisterModal, { redactionRegisterId: id });
  }, [id, openModal]);

  const { mutateAsync: generateCertificates } = useGenerateRegisterCertificatesMutation();

  const handleOpenDeleteRegisterModal = useCallback(() => {
    openModal<ConfirmModalProps>(ConfirmModal, {
      title: (
        <p>
          Are you sure you want to delete <span className="text-blue-700">{name}</span> ?
        </p>
      ),
      closeAfterConfirm: false,
      onConfirm: async () => {
        await deleteRegister({ id });
      },
    });
  }, [deleteRegister, id, name, openModal]);

  const handleGenerateRegisterCertificates = useCallback(() => {
    generateCertificates({ register_id: id });
  }, [generateCertificates, id]);

  const dropDownMenuOptions = useMemo(() => {
    return [
      {
        label: 'Edit',
        onClick: handleOpenRedactRegisterModal,
      },
      {
        label: 'Delete',
        onClick: handleOpenDeleteRegisterModal,
      },
      {
        label: 'Generate certificates',
        onClick: handleGenerateRegisterCertificates,
      },
    ];
  }, [
    handleGenerateRegisterCertificates,
    handleOpenDeleteRegisterModal,
    handleOpenRedactRegisterModal,
  ]);
  return (
    <div className="w-full flex flex-col rounded-xl border border-gray-200 px-2 pt-4 sm:px-6">
      <div className="w-full flex items-center justify-between pb-4 capitalize">
        <div className="flex items-center space-x-3">
          <Avatar firstName={name} lastName={String(id)} />
          <p className="text-[14px] leading-[22px]">{name}</p>
        </div>
        <div className="grid grid-columns-2 gap-6 items-center">
          <div className="flex flex-col items-start min-w-[120px] col-start-1 col-end-2">
            <p className="text-[12px] leading-[20px] text-gray-500">Total invested</p>
            <p className="text-[14px] leading-[22px]">
              {goodFormatUSDCurrency(total_invested || 0)}
            </p>
          </div>
          <div className="flex gap-4 items-center col-start-2 col-end-3">
            <DropdownMenu
              items={dropDownMenuOptions}
              button={
                <button className="flex items-center justify-center">
                  <img
                    src={DotsHorizontalBlueIcon}
                    alt="Loading..."
                    className="hover:cursor-pointer hover:opacity-80 min-w-[24px] min-h-[24px]"
                  />
                </button>
              }
              placement={index < 2 ? 'left-start' : 'left-end'}
              className="w-[180px] divide-white"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
